import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';

export function AddRobotDialog({ onClose }: { onClose: () => void }) {
  const [id, setId] = useState('drone-2');
  const [name, setName] = useState('');
  const [bindAddr, setBindAddr] = useState('0.0.0.0:14550');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = {
        id,
        name: name || undefined,
        adapter: { kind: 'mavlink_udp' as const, bind_addr: bindAddr },
      };
      const { authHeader } = await import('@/store/auth');
      const { hubFetch } = await import('@/lib/hubUrl');
      const res = await hubFetch('/robots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error(err.message ?? `HTTP ${res.status}`);
      }
      onClose();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-[440px] max-h-[calc(100vh-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border-default bg-surface-1 p-5 shadow-2xl"
        >
          <div className="mb-3 flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold">Register drone</Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-fg-muted hover:text-fg-primary" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="mb-4 text-xs text-fg-muted">
            Add a new robot to the Hub's registry. SITL drones use the default UDP bind.
          </Dialog.Description>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="r-id">Robot ID</Label>
              <Input id="r-id" value={id} onChange={(e) => setId(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="r-name">Display name (optional)</Label>
              <Input id="r-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={id} />
            </div>
            <div>
              <Label htmlFor="r-addr">UDP bind address</Label>
              <Input
                id="r-addr"
                value={bindAddr}
                onChange={(e) => setBindAddr(e.target.value)}
                placeholder="0.0.0.0:14550"
                required
              />
              <p className="mt-1 text-xs text-fg-muted">
                Hub binds this UDP port and listens for MAVLink frames from PX4 / ArduPilot
                (SITL or live). Default 14550 for the first drone.
              </p>
            </div>
            {error && (
              <div className="rounded border border-status-critical/40 bg-status-critical/10 p-2 text-xs text-status-critical">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy ? 'Registering…' : 'Register'}</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
